import { SAFETY_BRIEFING } from '../resourcesData'
import type { SafetyBriefingContent } from '../../../hooks/useResourceOverrides'

// Safety Briefing tab — first surface in the rebuilt ResourcesPanel.
// Mirrors prototype L3660-3682 exactly (per spec 23 v2 work plan):
// Safety Essentials lead, oxblood-soft Platform-Train Interface callout,
// Your Responsibilities bullets, Emergency Numbers card with tel: links.
//
// All copy is PUBLIC CONTRACT (engineering lessons §10) — defaults come
// from SAFETY_BRIEFING in resourcesData.ts; never paraphrase inline.
//
// `content` optional prop wires up the per-browser preview/tuning surface
// (Resources Edit Defaults). When provided, the tab renders the resolved
// override values; when omitted, it falls back to the constants directly
// so the component stays self-contained for tests / Storybook / etc.

interface Props {
  content?: SafetyBriefingContent
}

export function SafetyBriefingTab({ content }: Props) {
  const resolved: SafetyBriefingContent = content ?? {
    intro: SAFETY_BRIEFING.intro,
    callout: { title: SAFETY_BRIEFING.callout.title, body: SAFETY_BRIEFING.callout.body },
    responsibilities: [...SAFETY_BRIEFING.responsibilities],
    emergencyNumbers: SAFETY_BRIEFING.emergencyNumbers.map((n) => ({
      label: n.label, tel: n.tel, display: n.display
    }))
  }

  return (
    <div>
      <h3 className="font-display text-xl font-semibold mb-2">Safety Essentials</h3>
      <p className="text-sm text-ink-soft mb-4">{resolved.intro}</p>

      <div className="bg-oxblood-soft border border-oxblood rounded-lg p-3.5 mb-4">
        <div className="font-semibold text-oxblood">{resolved.callout.title}</div>
        <div className="text-sm text-ink-soft mt-1 leading-snug">
          {resolved.callout.body}
        </div>
      </div>

      <h4 className="text-xs uppercase font-bold text-ink-soft tracking-wider mt-4 mb-2">
        Your Responsibilities
      </h4>
      <ul className="list-disc pl-5 mb-4 space-y-1">
        {resolved.responsibilities.map((r, i) => (
          <li key={`${i}:${r}`} className="text-sm leading-snug">{r}</li>
        ))}
      </ul>

      <h4 className="text-xs uppercase font-bold text-ink-soft tracking-wider mt-4 mb-2">
        Emergency Numbers
      </h4>
      <div className="bg-parchment border border-line rounded-lg p-3">
        {resolved.emergencyNumbers.map((n, i) => (
          <div
            key={`${i}:${n.label}`}
            className={
              'flex items-center justify-between gap-3 ' +
              (i < resolved.emergencyNumbers.length - 1 ? 'mb-1.5' : '')
            }
          >
            <span className="font-semibold text-sm">{n.label}</span>
            <a
              href={`tel:${n.tel}`}
              className="font-mono text-sm text-green hover:underline"
            >
              {n.display}
            </a>
          </div>
        ))}
      </div>
    </div>
  )
}
